import type { FunctionComponent } from 'react'
import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useHistory } from 'react-router-dom'
import { CustomDropdown, SignUp, TypographyV2 as Typography } from '@which/seatbelt'
import type { DataLayerObject } from '@which/shared'
import { dynamicDatalayerPush } from '@which/shared'

import classnames from 'classnames'

import type { ProductListingPageQuery } from '../../../../../generated/frontend'
import { singleBrandUrlRegex } from '../../../../../shared/constants/single-brand-ulr-regex'
import { updateUrlQueryParams } from '../../../../../shared/utils/update-url-query-params'
import { modalImageProps } from '../../../shared/data'
import styles from './SortMenu.module.scss'

export const SortMenu: FunctionComponent<SortMenuProps> = ({
  className,
  items,
  defaultOption,
  ...rest
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedLabel, setSelectedLabel] = useState<string>('')
  const dataLayerEventLabel =
    selectedLabel === 'SCORE_DESC' ? 'lockedsortoptionbyscore' : 'lockedsortoptionbyprice'
  const history = useHistory()

  const optionHandler = ({
    label,
    value,
    icon,
  }: {
    label: string
    value: string
    icon?: string
  }) => {
    setSelectedLabel(value)

    if (icon) {
      setModalOpen(true)
    } else {
      let updatedUrl = updateUrlQueryParams({})

      // remove brand friendly url if present before appending sortBy param
      if (updatedUrl) {
        const isBrandFriendlyUrl = singleBrandUrlRegex.test(updatedUrl)

        if (isBrandFriendlyUrl) {
          const [, , , brand]: string[] = updatedUrl?.split('/')

          updatedUrl = updatedUrl.replace(
            singleBrandUrlRegex,
            `?filters.manufacturers.manufacturer=${brand}`
          )
          history.replace(updatedUrl, { updateQueryString: true })
        }
      }

      // append sortBy param
      updatedUrl = updateUrlQueryParams({ sortBy: value, page: '' })
      updatedUrl && history.replace(updatedUrl, { updateQueryString: true })
    }

    pushToDataLayer({
      eventLabel: `${label}${icon ? ' (locked)' : ''}`,
    })
  }

  const openCloseHandler = ({ showOptions }: { showOptions: boolean }) => {
    pushToDataLayer({
      eventLabel: showOptions ? 'dropdown open' : 'dropdown close',
    })
  }

  return (
    <span className={classnames(className, styles.sortMenuInSortby)} {...rest}>
      <span className={styles.sortMenuAlign}>
        <Typography
          tag="span"
          textStyle="sb-text-body-default-regular"
          className={styles.sortByTitle}
        >
          Sort by:
        </Typography>

        <span className={styles.customWrapper}>
          <CustomDropdown
            className={styles.dropdownContainer}
            classNameSelectedContainer={styles.selectedContainer}
            classNameOptionsContainer={styles.optionsContainer}
            options={items}
            defaultOption={defaultOption}
            handleSortByCallback={optionHandler}
            handleOpenCloseCallback={openCloseHandler}
            handleOptionWithIconCallback={optionHandler}
          />
        </span>
      </span>
      {modalOpen &&
        createPortal(
          <SignUp
            className={styles.modal}
            image={modalImageProps}
            overline="Recommended"
            title="Full access"
            closeModal={() => setModalOpen(false)}
            modalLinkDatalayerTrackingId={dataLayerEventLabel}
          />,
          document.body
        )}
    </span>
  )
}

///////// IMPLEMENTATION /////////

export type SortMenuProps = {
  className?: string
  items: ProductListingPageQuery['productListingPage']['sortByMenu']['items']
  defaultOption?: ProductListingPageQuery['productListingPage']['sortByMenu']['defaultOption']
}

const pushToDataLayer = ({
  event = 'tEvent',
  eventAction = 'sort by',
  eventCategory = 'refine results',
  eventLabel = '',
}: Partial<DataLayerObject>) => {
  dynamicDatalayerPush({
    event,
    eventAction,
    eventCategory,
    eventLabel,
  })
}
